import type { DashboardItems } from '@/pages/dashboard/UserDashboard'
import {
  Checkbox,
  createSlotRecipeContext,
  type HTMLChakraProps,
  Text,
} from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { LuMenu } from 'react-icons/lu'

import { dashboardControlRecipe } from '@/theme/recipes/dashboard-control.recipe'

const { withProvider, withContext } = createSlotRecipeContext({ recipe: dashboardControlRecipe })

const ControlRoot = withProvider<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'root')
const ControlItem = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'item')
const ControlHandle = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'handle')
const ControlPreview = withProvider<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'preview')

interface DashboardControlItem {
  value: DashboardItems
  label: string
  visible: boolean
  disabled: boolean
}

const initialItems: DashboardControlItem[] = [
  { value: 'activities', visible: true, label: 'Aktivitäten', disabled: false },
  { value: 'invoices', visible: true, label: 'Rechnungen', disabled: false },
  { value: 'orders', visible: true, label: 'Bestellungen', disabled: false },
  { value: 'workload', visible: true, label: 'Arbeitszeit', disabled: false },
  { value: 'notifications', visible: true, label: 'Benachrichtigungen', disabled: false },
]

function CustomDragLayer() {
  const { isDragging, item, initialOffset, currentOffset, clientOffset } = useDragLayer(
    monitor => ({
      item: monitor.getItem(),
      initialOffset: monitor.getInitialSourceClientOffset(),
      currentOffset: monitor.getSourceClientOffset(),
      clientOffset: monitor.getClientOffset(),
      isDragging: monitor.isDragging(),
    })
  )

  if (!isDragging) {
    return null
  }

  const layerStyles: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 100,
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  }

  function getItemStyles(initialOffset: any, currentOffset: any, clientOffset: any) {
    if (!initialOffset || !currentOffset || !clientOffset) {
      return {
        display: 'none',
      }
    }

    const dragHandleOffsetX = item?.handleOffsetX ?? -160
    const dragHandleOffsetY = item?.handleOffsetY ?? -10

    const { x, y } = clientOffset
    const transform = `translate(${x + dragHandleOffsetX}px, ${y + dragHandleOffsetY}px) scale(1.05)`
    return {
      transform,
      WebkitTransform: transform,
    }
  }

  return (
    <div style={layerStyles}>
      <div style={getItemStyles(initialOffset, currentOffset, clientOffset)}>
        <ControlPreview width={200}>
          <Checkbox.Root checked={item?.visible}>
            <Checkbox.Control />
            <Checkbox.Label>
              <Text>{item?.label}</Text>
            </Checkbox.Label>
          </Checkbox.Root>
          <LuMenu />
        </ControlPreview>
      </div>
    </div>
  )
}

interface DashboardControlProps {
  items: DashboardControlItem[]
  onChange: (items: DashboardControlItem[]) => void
}

export default function DashboardControl({ items, onChange }: DashboardControlProps) {
  const moveRow = useCallback((dragIndex: number, hoverIndex: number) => {
    setInternalItems(prev => {
      const dragItem = prev[dragIndex]
      const next = [...prev]
      next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, dragItem)
      return next
    })
  }, [])

  const [internalItems, setInternalItems] = useState(items)

  useEffect(() => {
    setInternalItems(items)
  }, [items])

  return (
    <DndProvider backend={HTML5Backend}>
      <ControlRoot data-testid="dashboard-control">
        {internalItems.map((widget, index) => (
          <DashboardControlDraggable
            key={widget.value}
            item={widget}
            index={index}
            moveRow={moveRow}
            toggleVisibility={() => {
              onChange(items.map((w, i) => (i === index ? { ...w, visible: !w.visible } : w)))
            }}
            onDropEnd={() => onChange(internalItems)}
          />
        ))}
      </ControlRoot>
      <CustomDragLayer />
    </DndProvider>
  )
}

interface DashboardControlDraggableProps {
  item: DashboardControlItem
  index: number
  moveRow: (dragIndex: number, hoverIndex: number) => void
  toggleVisibility?: () => void
  onDropEnd: () => void
}

function DashboardControlDraggable({
  item,
  index,
  moveRow,
  toggleVisibility,
  onDropEnd,
}: DashboardControlDraggableProps) {
  const { visible, value } = item
  const ref = useRef<HTMLDivElement>(null)

  const [, drop] = useDrop<DashboardControlDraggableProps, DashboardControlDraggableProps>({
    accept: 'widget-control',
    collect(monitor) {
      return {
        isOver: monitor.isOver(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) {
        return
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset() || { x: 0, y: 0 }
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      moveRow(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: 'widget-control',
    item: { index },
    end: () => {
      onDropEnd()
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drop(ref)

  const dragHandleRef = useRef<HTMLDivElement>(null)
  drag(dragHandleRef)

  return (
    <ControlItem
      ref={ref}
      width="100%"
      minWidth={200}
      data-testid={`dashboard-control-item-${item.value}`}
      data-dragging={isDragging ? '' : undefined}
    >
      <Checkbox.Root
        data-testid={`dashboard-control-checkbox-${item.value}`}
        checked={visible}
        onCheckedChange={toggleVisibility}
        disabled={item.disabled}
      >
        <Checkbox.HiddenInput />
        <Checkbox.Control />
        <Checkbox.Label>
          <Text>{item.label}</Text>
        </Checkbox.Label>
      </Checkbox.Root>
      <ControlHandle
        ref={dragHandleRef}
        data-testid={`dashboard-control-drag-handle-${item.value}`}
        data-dragging={isDragging ? '' : undefined}
      >
        <LuMenu />
      </ControlHandle>
    </ControlItem>
  )
}
